// 从 saas-editor/src/imageUpload/index.tsx 搬迁
import React, { useImperativeHandle } from 'react';
import { Dragger, Message as message, Tooltip, Upload } from '@meitu/saas-ui';
import type { UploadChangeParam, UploadFile } from '@meitu/saas-ui/es/upload/interface';
import {
    getWebmInfoAsync,
    IMAGE_TYPE,
    isWebm as isWebmFn,
    loadImageAsync,
    svg2Png,
    uploadVectorFile,
} from '@meitu/saas-editor-core/es/utils/file-kit';
import { uploadFileToObs } from '@meitu/saas-editor-core/es/utils/upload-kit';
import { parseVector } from '@meitu/saas-editor-core/es/utils/vector-kit';
import { getFileType, imageUploader, UploadStatusCode, type FileType } from '@meitu/xdesign-widgets/dist/uploadManager';
import './index.less';
import { v4 as uuid } from 'uuid';
import { IVectorMaterialInfo, MAX_STAGE_HEIGHT, MAX_STAGE_WIDTH } from '@meitu/saas-editor-core/es/version-types';
import { useLocaleStore } from '@/i18n';

export type saveSvgParams = {
    image: string;
    file: string;
    info: IVectorMaterialInfo;
};
export interface ImageUploadProps {
    fetchSvgInfo?: boolean;
    convertToWebp?: boolean;
    /**
     * 成功回调方法
     */
    onSuccess: (
        url: string,
        options: {
            width: number;
            height: number;
            svgInfo?: saveSvgParams;
            fileName?: string;
            fileSize?: number;
        },
        uid: string,
        image?: HTMLImageElement,
    ) => void;
    /**
     * 错误回调方法
     */
    onError?: (err: any, fileData?: any) => void;
    /**
     * change事件回调方法
     */
    onChange?: (v: UploadFile) => void;
    /**
     * 禁用
     */
    disabled?: boolean;

    /**
     * 要展示的
     */
    children?: React.ReactNode;

    /**
     * 上传前回调参数
     */
    beforeUpload?: Function;

    /**
     * 提示语
     */
    tips?: string;

    /**
     * 上传文件数量
     */
    maxCount?: number;
    /**
     * 提示语位置
     */

    tipsPlacement?:
        | 'top'
        | 'left'
        | 'right'
        | 'bottom'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight'
        | 'leftTop'
        | 'leftBottom'
        | 'rightTop'
        | 'rightBottom';
    /**
     * 自定义请求配置
     */
    // customRequestConfig: ICustomRequestConfigProps;
    btnText?: string;
    messageText?: string | React.ReactElement;
    card?: boolean;
    drop?: boolean;
    obsType?: string;
    fileKey?: string;
    // 返回字段
    responseField?: string;
    maxSize?: number;
    // 是否自动转换 webp
    autoConvertWebp?: boolean;
    // 配合 autoConvertWebp 使用, 设置转换的目标格式
    convertType?: 'image/png' | 'image/jpeg';
}

export interface ImageUploadRef {
    getUploadConfig: () => any;
}
// 最大文件限制
const MAX_FILE_SIZE = 20;

const ImageUpload = React.forwardRef<ImageUploadRef, ImageUploadProps>(
    (
        {
            onSuccess,
            onChange,
            onError,
            beforeUpload,
            disabled,
            children,
            tips,
            tipsPlacement = 'bottom',
            maxCount = 1,
            btnText,
            messageText,
            card = false,
            drop = false,
            obsType,
            fileKey,
            responseField = 'url',
            maxSize,
            convertToWebp = true,
            autoConvertWebp = false,
            convertType,
            fetchSvgInfo = true,
            // customRequestConfig,
        },
        ref,
    ) => {
        const { $t } = useLocaleStore();
        btnText = btnText ?? $t((v) => v.imageUpload.btnText);
        // 支持上传的图片格式
        const WIDGETS_IMAGE_TYPE = ['image/png', 'image/jpg', 'image/jpeg', 'image/heic', 'image/webp', 'image/gif'];
        // 上传配置
        const uploadConf = {
            name: 'file',
            accept: IMAGE_TYPE.join(','),
            disabled,
            maxCount,
            // 是否开启多选,如果maxCount大于1开启
            multiple: maxCount > 1 ? true : false,
            showUploadList: false,
            // listType: "picture-card",
            headers: {
                authorization: 'authorization-text',
            },

            async customRequest(fileData: any) {
                try {
                    const uid = fileData.file?.uid;
                    const fileType = fileData.file ? await getFileType(fileData.file) : '';

                    let svgInfo;
                    // 通用图片走widgets图片上传。有降级云处理策略
                    if (WIDGETS_IMAGE_TYPE.includes(fileType)) {
                        const { url, width, height } = await imageUploader.checkAndUploadFileToObs(fileData.file, {
                            customMaatType: obsType,
                            autoConvertWebp,
                            autoConvertGif: false,
                            convertType,
                            limitConfig: {
                                autoResizeWH: MAX_STAGE_WIDTH,
                                maxSize: (maxSize || MAX_FILE_SIZE) * 1024 * 1024,
                                accepts: WIDGETS_IMAGE_TYPE as FileType[],
                            },
                        });
                        onSuccess(
                            url,
                            {
                                width,
                                height,
                                fileName: fileData.file.name,
                                fileSize: fileData.file.size,
                            },
                            uid,
                        );
                    } else {
                        if (fileType === 'image/svg+xml') {
                            try {
                                const { pngFile, svgEl } = await svg2Png(fileData.file, true);
                                if (fetchSvgInfo) {
                                    const { text, info } = await parseVector(svgEl);
                                    const imageUrl = await uploadVectorFile(text);
                                    svgInfo = {
                                        image: imageUrl.url,
                                        info,
                                        file: fileData.file.url,
                                    };
                                }
                                fileData.file = pngFile;
                            } catch (error) {
                                console.error('转换失败', error);
                                throw error;
                            }
                        }

                        const fileExtension = fileData.file.name.split('.').pop()?.toLowerCase() || 'jpg';
                        const data = await uploadFileToObs(
                            fileData.file,
                            fileKey ? `${fileKey}${uuid()}` : undefined,
                            obsType,
                            fileExtension,
                        );
                        const _url = data[responseField];

                        const isWebm = isWebmFn(_url);

                        const info: any = await (isWebm ? getWebmInfoAsync(_url) : loadImageAsync(_url, false));

                        const width = isWebm ? info.videoWidth : info.width; //图片的宽
                        const height = isWebm ? info.videoHeight : info.height; //图片的高

                        // 判断尺寸
                        const sizeLimit = width <= MAX_STAGE_WIDTH && height <= MAX_STAGE_HEIGHT;

                        if (!sizeLimit) {
                            message.error(
                                $t((v, { r }) => r(v.imageUpload.sizeLimit, [MAX_STAGE_WIDTH, MAX_STAGE_HEIGHT])),
                            );
                            onError && onError('dimensions', fileData);
                            return;
                        }

                        onSuccess(
                            info.src,
                            {
                                width,
                                height,
                                svgInfo,
                                fileName: fileData.file.name,
                                fileSize: fileData.file.size,
                            },
                            uid,
                            info,
                        );
                    }
                } catch (error: any) {
                    let errMsg = 'network';
                    if (error.code === UploadStatusCode.TypeError) {
                        message.error($t((v) => v.imageUpload.supportImageType));
                        errMsg = 'type';
                    }
                    if (error.code === UploadStatusCode.FileTooLarge) {
                        message.error($t((v) => v.imageUpload.supportImageType));
                        errMsg = 'size';
                    }
                    if (error.code === UploadStatusCode.ImageTooLarge) {
                        message.error($t((v) => v.imageUpload.supportImageType));
                        errMsg = 'dimensions';
                    }
                    if (error.code === UploadStatusCode.ConvertError) {
                        message.error($t((v) => v.imageUpload.heicErrorText));
                        errMsg = 'convert';
                    }
                    onError && onError(errMsg, fileData);
                }
            },
            // 上传图片前
            async beforeUpload(file: any, upFileList: any[]) {
                if (maxCount && upFileList.length > maxCount) {
                    message.error($t((v, { r }) => r(v.imageUpload.beforeUploadError, [maxCount])));
                    return false;
                }

                const fileType = await getFileType(file);

                // 判断类型
                const isJpgOrPngOrHeic = IMAGE_TYPE.includes(fileType);
                // 判断大小
                const isLt20M = file.size / 1024 / 1024 < (maxSize || MAX_FILE_SIZE);

                if (!isLt20M) {
                    message.error($t((v, { r }) => r(v.imageUpload.supportImageSize, [maxSize || MAX_FILE_SIZE])));
                    onError && onError('size');
                }

                if (!isJpgOrPngOrHeic) {
                    message.error($t((v) => v.imageUpload.supportImageType));
                    onError && onError('type');
                }

                if (isLt20M && isJpgOrPngOrHeic) {
                    try {
                        beforeUpload && (await beforeUpload(upFileList));
                    } catch (error) {
                        return false;
                    }
                }
                return isJpgOrPngOrHeic && isLt20M;
            },

            onDrop(e: any) {
                console.log('Dropped files', e.dataTransfer.files);
                for (let i = 0; i < e.dataTransfer.files.length; i++) {
                    const file = e.dataTransfer.files[i];
                    uploadConf.beforeUpload(file, e.dataTransfer.files);
                }
            },

            onChange(info: UploadChangeParam) {
                console.log('onchange');
                // 不使用ant的上传
                if (info.file.status === 'uploading') {
                    onChange && onChange(info.file);
                    // console.log(info.file, info.fileList,'>>>>111');
                }

                if (info.file.status === 'done') {
                    message.success(`${info.file.name} file uploaded successfully`);
                } else if (info.file.status === 'error') {
                    message.error(`${info.file.name} file upload failed.`);
                }
            },
            tips,
        };

        useImperativeHandle(
            ref,
            () => ({
                getUploadConfig: () => {
                    return uploadConf;
                },
            }),
            [],
        );

        const renderInnerContent = () => {
            return (
                <div>
                    <div className="img-upload__children">
                        <Tooltip title={tips} placement={tipsPlacement}>
                            {children}
                        </Tooltip>

                        {messageText ? <div className="img-upload__message">{messageText}</div> : null}
                    </div>
                </div>
            );
        };

        return drop ? (
            <Dragger className={card ? 'img-upload' : ''} {...uploadConf}>
                {renderInnerContent()}
            </Dragger>
        ) : (
            <Upload className={card ? 'img-upload' : ''} {...uploadConf}>
                {renderInnerContent()}
            </Upload>
        );
    },
);

export default ImageUpload;
